/**
 * The category pictograms (FR-04), inlined as SVG.
 *
 * Split from `PlantIcons` when that file outgrew the 350-line contract. The seam
 * is the subject rather than the size: these six glyphs answer "what KIND of
 * plant is this", and they change when the category registry changes. The ones
 * left behind answer "what is this plant LIKE", and change when the content
 * model does.
 *
 * `TreeIcon` is not among them. It belongs to both vocabularies — the `trees`
 * category and the size pictogram for anything measured in metres — and a glyph
 * that means two things must still be drawn once, so it stays with the
 * properties and is imported here.
 *
 * Drawn in the shared `icons/Icon` frame: 1.5px strokes, round caps, botanical
 * geometry. Shape names and props are the contract; paths are not.
 */

import Icon from '../icons/Icon';
import { TreeIcon } from './PlantIcons';

export function ShrubIcon(props) {
    return (
        <Icon {...props}>
            <path d="M3.4 19.5h17.2" />
            <path d="M20.2 19.5c0-1.2-.7-2.2-1.8-2.6.3-.5.5-1 .5-1.5 0-1.7-1.5-2.9-3.2-2.6-.6-1.5-2-2.5-3.7-2.5s-3.1 1-3.7 2.5c-1.7-.3-3.2.9-3.2 2.6 0 .5.2 1 .5 1.5-1.1.4-1.8 1.4-1.8 2.6" />
            <path d="M12 19.5v-3.6" />
            <path d="m12 17.4-3-2.1M12 17.4l3-2.1M12 18.1l-1.6-2.8M12 18.1l1.6-2.8" />
        </Icon>
    );
}

/**
 * Five petals with one pointing DOWN, so the rosette opens with a notch at the
 * top — the arrangement the reference draws, and the reason its flower reads as
 * a flower rather than as a cluster of circles. The petals stop just short of
 * the centre; the small hole they leave is the glyph's eye.
 */
export function FlowerIcon(props) {
    return (
        <Icon {...props}>
            <ellipse cx="12" cy="16.6" rx="3" ry="3.8" />
            <ellipse cx="12" cy="16.6" rx="3" ry="3.8" transform="rotate(72 12 12)" />
            <ellipse cx="12" cy="16.6" rx="3" ry="3.8" transform="rotate(144 12 12)" />
            <ellipse cx="12" cy="16.6" rx="3" ry="3.8" transform="rotate(216 12 12)" />
            <ellipse cx="12" cy="16.6" rx="3" ry="3.8" transform="rotate(288 12 12)" />
        </Icon>
    );
}

/**
 * A tuft, not a fan. Six near-upright blades rising from a short base with the
 * middle ones tallest — blades radiating from one point read as an asterisk at
 * 20px, which is the size this is actually seen at.
 */
export function GrassIcon(props) {
    return (
        <Icon {...props}>
            <path d="M8 20.8c-.2-3-.8-5.3-1.8-7.3" />
            <path d="M9.6 20.8c0-4-.4-7.5-.8-10.8" />
            <path d="M11.4 20.8c-.4-4.5-.4-8.5 0-12.6" />
            <path d="M13 20.8c.2-4.4.6-8.2 1.2-11.8" />
            <path d="M14.8 20.8c.4-3.4 1-6.4 1.8-9.2" />
            <path d="M16.4 20.8c.5-2.4 1.2-4.6 2-6.4" />
        </Icon>
    );
}

/** `rock-dry-garden` — stones, because the defining feature is what it is not: soil. */
export function PebbleIcon(props) {
    return (
        <Icon {...props}>
            <path d="M2.8 19.5c0-2.6 2.1-4.7 4.8-4.7s4.8 2.1 4.8 4.7Z" />
            <path d="M12.4 19.5c0-2 1.7-3.6 3.9-3.6s3.9 1.6 3.9 3.6Z" />
            <path d="M8.6 12.6c0-1.9 1.6-3.5 3.6-3.5s3.6 1.6 3.6 3.5Z" />
        </Icon>
    );
}

/** `groundcovers` — a plant that spreads sideways rather than up. */
export function MatIcon(props) {
    return (
        <Icon {...props}>
            <path d="M3.4 19.6h17.2" />
            <path d="M12 19.6c0-2.4 1.8-4 5.1-4.4-.3 3-2 4.4-5.1 4.4Z" />
            <path d="M12 19.6c0-2.4-1.8-4-5.1-4.4.3 3 2 4.4 5.1 4.4Z" />
            <path d="M12 19.6v-2.7" />
        </Icon>
    );
}

/**
 * `herbs-edibles` — a sprig with opposite leaves in pairs.
 *
 * The shape had to be legible at 20 px against six glyphs that already exist,
 * which rules out most of what "herb" suggests: a leafy dome is `ShrubIcon`, an
 * upright tuft is `GrassIcon`, a rosette is `FlowerIcon` and two leaves lying
 * flat are `MatIcon`. Paired opposite leaves up one stem are none of those, and
 * they are also the actual signature of the family most culinary herbs come from
 * — basil, mint, thyme, sage, oregano are all Lamiaceae, and all of them are
 * built this way.
 *
 * TWO pairs, not three, and drawn at `MatIcon`'s leaf size rather than smaller.
 * Three pairs was the first attempt and it collapsed into a dark blob at 20 px —
 * the size this is actually seen at — because a 1.5 px stroke closes up any
 * counter narrower than about four units. The glyphs that survive that size all
 * keep their shapes big and few; this one now does too.
 *
 * No ground line: the categories that draw ground draw it because the plant's
 * relationship to the soil is the point (`MatIcon`, `ShrubIcon`). An edible one
 * is defined by what you cut off it.
 */
export function HerbIcon(props) {
    return (
        <Icon {...props}>
            <path d="M12 20.8V6.2" />
            <path d="M12 17c0-2.4 1.8-4 5.1-4.4-.3 3-2 4.4-5.1 4.4Z" />
            <path d="M12 17c0-2.4-1.8-4-5.1-4.4.3 3 2 4.4 5.1 4.4Z" />
            <path d="M12 11c0-1.9 1.4-3.2 4.1-3.5-.2 2.4-1.6 3.5-4.1 3.5Z" />
            <path d="M12 11c0-1.9-1.4-3.2-4.1-3.5.2 2.4 1.6 3.5 4.1 3.5Z" />
        </Icon>
    );
}

/**
 * One glyph per category (FR-04), used by both a shelf's heading and the corner
 * badge on a card — the badge is only legible as "what kind of plant is this"
 * because the heading above it taught the same shape a second earlier.
 *
 * `climbers` is absent on purpose: it has no content pack, so no shelf and no
 * filter option is built for it, and a glyph drawn for a category nothing can
 * reach is code with no caller. `herbs-edibles` was in that sentence until Basil
 * arrived (SPEC-002 PR-2.22) and is now drawn like any other shelf.
 */
export const CATEGORY_ICONS = {
    trees: TreeIcon,
    shrubs: ShrubIcon,
    perennials: FlowerIcon,
    grasses: GrassIcon,
    'herbs-edibles': HerbIcon,
    'rock-dry-garden': PebbleIcon,
    groundcovers: MatIcon,
};
